import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, PlayCircle } from "lucide-react";
import { api, type ApiCategory, type ApiVideo } from "@/lib/api";

export default function CheckinsPage() {
  const [videos, setVideos] = useState<ApiVideo[]>([]);
  const [cats, setCats] = useState<ApiCategory[]>([]);
  const [done, setDone] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.listVideos(), api.listCategories(), api.myCheckins()])
      .then(([v, c, k]) => {
        setVideos(v.videos);
        setCats(c.categories);
        setDone(new Set(k.completedVideoIds));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<number, ApiVideo[]>();
    videos.forEach((v) => {
      const arr = map.get(v.category_id) || [];
      arr.push(v);
      map.set(v.category_id, arr);
    });
    return cats
      .map((c) => ({ cat: c, vids: map.get(c.id) || [] }))
      .filter((g) => g.vids.length > 0);
  }, [videos, cats]);

  const total = videos.length;
  const completedCount = videos.filter((v) => done.has(v.id)).length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  if (loading)
    return <main className="container-narrow py-20 text-center text-sm text-ink-400">加载中…</main>;

  return (
    <main className="container-narrow py-10 lg:py-14">
      <div className="mb-8 border-b border-surface-border pb-8">
        <div className="badge mb-3">学习打卡</div>
        <h1 className="font-display text-3xl font-semibold text-ink-900 md:text-4xl">我的学习打卡</h1>
        <p className="mt-2 text-sm text-ink-400">看完一个视频，该视频即自动打卡。按视频记录完成进度。</p>

        <div className="mt-6 flex items-center gap-6">
          <div>
            <div className="font-display text-3xl font-semibold text-ink-900">
              {completedCount}
              <span className="text-lg text-ink-400"> / {total}</span>
            </div>
            <div className="text-xs text-ink-400">已完成视频</div>
          </div>
          <div className="flex-1">
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-muted">
              <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-1 text-right text-xs text-ink-400">完成度 {pct}%</div>
          </div>
        </div>
      </div>

      {total === 0 ? (
        <div className="rounded-xl border border-dashed border-surface-border bg-white py-20 text-center text-sm text-ink-400">
          还没有可学习的视频。
        </div>
      ) : (
        <div className="space-y-10">
          {grouped.map(({ cat, vids }) => {
            const cDone = vids.filter((v) => done.has(v.id)).length;
            return (
              <section key={cat.id}>
                <div className="mb-4 flex items-baseline justify-between">
                  <h2 className="font-display text-xl font-semibold text-ink-900">{cat.name}</h2>
                  <span className="text-xs text-ink-400">
                    {cDone} / {vids.length} 已完成
                  </span>
                </div>
                <div className="overflow-hidden rounded-xl border border-surface-border bg-white divide-y divide-surface-border">
                  {vids.map((v) => {
                    const ok = done.has(v.id);
                    return (
                      <Link
                        key={v.id}
                        to={`/watch/${v.id}`}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-surface-muted/40"
                      >
                        {ok ? (
                          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />
                        ) : (
                          <Circle className="h-5 w-5 shrink-0 text-ink-400/40" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate text-sm ${ok ? "text-ink-900" : "text-ink-700"}`}>
                            {v.title}
                          </span>
                          {v.description && (
                            <span className="block truncate text-xs text-ink-400">{v.description}</span>
                          )}
                        </span>
                        {ok ? (
                          <span className="shrink-0 text-[11px] font-semibold text-emerald-600">已打卡</span>
                        ) : (
                          <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-ink-400">
                            <PlayCircle className="h-3.5 w-3.5" /> 去学习
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </main>
  );
}
